<template>
    <v-container class="overflow-auto pa-5 ma-0" style="max-width: 900px">
        <v-form ref="form" @input="$emit('input', $event)">
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <h4>{{ $t('organizer') }}</h4>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <v-radio-group
                    dense
                    row
                    hide-details="auto"
                    :value="record.legal_type"
                    :rules="[getRule('required')]"
                    @change="onInput('legal_type', $event)"
                >
                    <v-radio :label="$t('organization_label')" value="organization" />
                    <v-radio :label="$t('person_label')" value="person" />
                </v-radio-group>
            </v-row>
            <v-row
                v-if="record?.legal_type === RequestedUser.LEGAL_TYPE_ORGANIZATION"
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-2"
            >
                <!-- title -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('bezeichnung')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.title"
                            counter="256"
                            :rules="[getRule('required'), getRule('maxLength')(256)]"
                            dense
                            outlined
                            @input="onInput('title', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- description -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('description')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.description"
                            :rules="[getRule('required')]"
                            dense
                            outlined
                            @input="onInput('description', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-if="record?.legal_type === RequestedUser.LEGAL_TYPE_PERSON"
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-2"
            >
                <!-- firstname -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('vorname')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.firstname"
                            counter="50"
                            :rules="[getRule('maxLength')(50)]"
                            dense
                            outlined
                            @input="onInput('firstname', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- lastname -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('name')">
                        <v-text-field
                            hide-details="auto"
                            :value="record?.lastname"
                            counter="50"
                            :rules="[getRule('maxLength')(50)]"
                            dense
                            outlined
                            @input="onInput('lastname', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>

            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <h4>{{ $t('adresse') }}</h4>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <!-- street -->
                <v-col cols="12" lg="4" xl="5" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('strasse')">
                        <v-text-field
                            :value="record?.street"
                            dense
                            outlined
                            counter="100"
                            :rules="[getRule('maxLength')(100)]"
                            hide-details="auto"
                            @input="onInput('street', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- street_nr -->
                <v-col cols="12" lg="2" xl="1" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('strasse_nr')">
                        <v-text-field
                            :value="record?.street_nr"
                            dense
                            outlined
                            hide-details="auto"
                            counter="15"
                            :rules="[getRule('maxLength')(15)]"
                            @input="onInput('street_nr', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- plz (zip) -->
                <v-col cols="12" lg="2" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('plz')">
                        <v-text-field
                            :value="record?.zip"
                            dense
                            outlined
                            hide-details="auto"
                            counter="10"
                            :rules="[getRule('between')(1000, 99999)]"
                            @input="onInput('zip', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- city -->
                <v-col cols="12" lg="4" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('ort')">
                        <v-text-field
                            :value="record?.city"
                            dense
                            outlined
                            hide-details="auto"
                            counter="100"
                            :rules="[getRule('maxLength')(100)]"
                            @input="onInput('city', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <!-- address_supplement -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('adresszusatz')">
                        <v-text-field
                            :value="record?.address_supplement"
                            dense
                            outlined
                            hide-details="auto"
                            counter="100"
                            :rules="[getRule('maxLength')(100)]"
                            @input="onInput('address_supplement', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- po_box -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('postfach')">
                        <v-text-field
                            :value="record?.po_box"
                            dense
                            outlined
                            hide-details="auto"
                            counter="100"
                            :rules="[getRule('maxLength')(100)]"
                            @input="onInput('po_box', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <!-- country_id -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('land')">
                        <CountrySelectField
                            :value="record?.country_id"
                            hide-details="auto"
                            item-value="id"
                            :label="null"
                            @input="onInput('country_id', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }"></v-col>
            </v-row>

            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-4"
            >
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <h4>{{ $t('contact') }}</h4>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <!-- tel -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('tel')">
                        <v-text-field
                            :value="record?.tel"
                            dense
                            outlined
                            hide-details="auto"
                            counter="50"
                            :rules="[getRule('maxLength')(50)]"
                            @input="onInput('tel', $event)"
                        />
                    </LabelField>
                </v-col>
                <!-- mobile -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('tel_2')">
                        <v-text-field
                            :value="record?.mobile"
                            dense
                            outlined
                            hide-details="auto"
                            counter="50"
                            :rules="[getRule('maxLength')(50)]"
                            @input="onInput('mobile', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <v-row
                v-bind="{
                    dense: !$vuetify.breakpoint.mdAndDown,
                    noGutters: $vuetify.breakpoint.mdAndDown,
                }"
                class="mt-0"
            >
                <!-- mail -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('email')">
                        <v-text-field
                            :value="record?.email"
                            dense
                            outlined
                            hide-details="auto"
                            counter="256"
                            :rules="[getRule('email'), getRule('maxLength')(256)]"
                            @input="onInput('email', $event)"
                        >
                            <template #append>
                                <a :href="`mailto:${record.email}`">
                                    <KpIcon icon-cls="link" color="primary" disabled-color="grey" />
                                </a>
                            </template>
                        </v-text-field>
                    </LabelField>
                </v-col>
                <!-- web -->
                <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                    <LabelField :label="$t('webseite')">
                        <UrlField
                            :value="record?.web"
                            counter="256"
                            :rules="[getRule('maxLength')(256)]"
                            @input="onInput('web', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
import { RequestedUser } from '@/models/RequestedUser';
import getRulesMixin from '@/mixins/get-form-rules';
import CountrySelectField from '@/components/country/SelectField.vue';

export default {
    name: 'RequestedUserOrganizerData',
    components: {
        CountrySelectField,
    },
    mixins: [getRulesMixin],

    props: {
        record: {
            type: RequestedUser,
            required: true,
        },
    },

    created() {
        this.RequestedUser = RequestedUser;
    },

    mounted() {
        this.validateForm();
    },

    methods: {
        validateForm() {
            this.$refs.form.validate();
        },

        onInput(field, value) {
            this.record?.set(field, value);
        },
    },
};
</script>

<style lang="scss" scoped></style>
